import type { AxiosError } from 'axios'

export interface ParsedError {
  message: string
  field?: string
}

export function parseApiError(error: unknown): ParsedError {
  if (typeof error === 'string') {
    return { message: error }
  }

  if (error && typeof error === 'object') {
    const directError = error as {
      error?: string | Record<string, string[]>
      message?: string
    }

    if (typeof directError.error === 'string') {
      return { message: directError.error }
    }

    if (typeof directError.error === 'object' && directError.error !== null) {
      const keys = Object.keys(directError.error)
      if (keys.length > 0) {
        const firstField = keys[0]!
        const fieldValue = directError.error[firstField]
        if (fieldValue) {
          const firstError =
            Array.isArray(fieldValue) && fieldValue.length > 0 ? fieldValue[0]! : String(fieldValue)

          return {
            message: firstError,
            field: firstField,
          }
        }
      }
    }

    if (typeof directError.message === 'string' && directError.message.trim()) {
      return { message: directError.message }
    }
  }

  const axiosError = error as AxiosError<{
    error?: string | Record<string, string[]>
    message?: string
  }>

  if (axiosError.response?.data) {
    const errorData = axiosError.response.data

    if (typeof errorData.error === 'string') {
      return { message: errorData.error }
    }

    if (errorData.message) {
      return { message: errorData.message }
    }

    if (typeof errorData.error === 'object' && errorData.error !== null) {
      const errorObj = errorData.error
      const keys = Object.keys(errorObj)
      if (keys.length > 0) {
        const firstField = keys[0]!
        const fieldValue = errorObj[firstField]
        if (fieldValue) {
          const firstError =
            Array.isArray(fieldValue) && fieldValue.length > 0 ? fieldValue[0]! : String(fieldValue)

          return {
            message: firstError,
            field: firstField,
          }
        }
      }
    }
  }

  if (error instanceof Error) {
    return { message: error.message }
  }

  return { message: 'An unexpected error occurred' }
}

export function parseFieldError(error: unknown, fieldName: string): string | undefined {
  const parsed = parseApiError(error)

  if (parsed.field === fieldName) {
    return parsed.message
  }

  const axiosError = error as AxiosError<{
    error?: Record<string, string[]>
  }>

  if (axiosError.response?.data?.error?.[fieldName]) {
    const fieldErrors = axiosError.response.data.error[fieldName]
    return Array.isArray(fieldErrors) ? fieldErrors[0] : String(fieldErrors)
  }

  return undefined
}
